import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle, ArrowRight, Clock, Users, BookOpen, Mic, Star, User, Calculator, Target, Heart, Sparkles, Scale, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LEVEL_ICONS = { beginner: BookOpen, intermediate: Mic, advanced: Star };
const LEVEL_ORDER = ['beginner', 'intermediate', 'advanced'];
const LEVEL_INFO = {
  sv: {
    beginner: { title: 'Nybörjare', subtitle: 'Quran Online Method', desc: 'Lär dig grunderna i arabisk läsning och uttal.' },
    intermediate: { title: 'Medelnivå', subtitle: 'Koranläsning med Tajweed', desc: 'Fördjupa din koranläsning med tajweed-reglerna.' },
    advanced: { title: 'Avancerad', subtitle: 'Koranmemorering (Hifdh)', desc: 'Memorera Koranen med erfarna lärare.' },
  },
  en: {
    beginner: { title: 'Beginner', subtitle: 'Quran Online Method', desc: 'Learn the fundamentals of Arabic reading and pronunciation.' },
    intermediate: { title: 'Intermediate', subtitle: 'Quran Reading with Tajweed', desc: 'Deepen your Quran reading with tajweed rules.' },
    advanced: { title: 'Advanced', subtitle: 'Quran Memorization (Hifdh)', desc: 'Memorize the Quran with experienced teachers.' },
  }
};

const FALLBACK_COURSES = [
  { id: 'noorani-qaida', name: 'Quran Online Method', level: 'beginner', price_per_lesson: 125, old_group_price: 145, private_enabled: true, private_price_min: 200, private_price_max: 220, description: 'Grundläggande arabisk läsning och uttal' },
  { id: 'tajweed', name: 'Tajweed', level: 'intermediate', price_per_lesson: 135, old_group_price: 150, private_enabled: true, private_price_min: 220, private_price_max: 250, description: 'Korrekt recitation med tajweed-regler' },
  { id: 'hifdh', name: 'Hifdh', level: 'advanced', price_per_lesson: 150, old_group_price: 180, private_enabled: true, private_price_min: 250, private_price_max: 300, description: 'Koranmemorering med erfaren lärare' },
];

const ProgramsPage = () => {
  const { language } = useLanguage();
  const sv = language === 'sv';
  const [courses, setCourses] = useState(FALLBACK_COURSES);
  const [activeLevel, setActiveLevel] = useState('beginner');
  const [classType, setClassType] = useState('group');
  const [lessonsPerWeek, setLessonsPerWeek] = useState(2);

  useEffect(() => {
    fetch(`${API_URL}/api/public/courses`).then(r => r.json()).then(d => { if (d?.length) setCourses(d); }).catch(() => {});
  }, []);

  const activeCourse = courses.find(c => c.level === activeLevel);
  const levelInfo = LEVEL_INFO[language]?.[activeLevel] || {};
  const priceMin = classType === 'private' && activeCourse?.private_enabled ? activeCourse.private_price_min : activeCourse?.price_per_lesson || 0;
  const priceMax = classType === 'private' && activeCourse?.private_enabled ? activeCourse.private_price_max : activeCourse?.price_per_lesson || 0;
  const isRange = priceMin !== priceMax;
  const monthlyCostMin = priceMin * lessonsPerWeek * 4;

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="programs-page">

      {/* Hero */}
      <section className="py-20 islamic-pattern hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-6 tracking-wider"
            style={{ backgroundColor: 'rgba(11,61,46,0.08)', color: '#0B3D2E', border: '1px solid rgba(11,61,46,0.15)' }}>
            <BookOpen size={14} />
            {sv ? 'KURSER & PRISER' : 'COURSES & PRICING'}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'En investering i din resa' : 'An investment in your journey'}
            <br />
            <span className="text-gold">{sv ? 'med Koranen' : 'with the Quran'}</span>
          </h1>
          <p style={{ color: 'var(--text-secondary)' }} className="max-w-2xl mx-auto mb-4 text-base leading-relaxed">
            {sv ? 'Att lära sig Koranen är inte en vanlig tjänst \u2014 det är en resa för att komma närmare Allah och lära sig läsa Hans ord korrekt.'
                 : 'Learning the Quran is not an ordinary service \u2014 it is a journey to come closer to Allah and learn to read His words correctly.'}
          </p>
          <p className="text-sm max-w-xl mx-auto" style={{ color: 'var(--text-muted)' }}>
            {sv ? 'Samtidigt kräver undervisning tid, ansvar och engagemang.'
                 : 'At the same time, teaching requires time, responsibility and commitment.'}
          </p>
        </div>
      </section>

      {/* Intention + Value sections */}
      <section className="py-16" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 mb-8">

            {/* Avsikten */}
            <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <Target size={22} className="text-gold" />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Avsikten är det viktigaste' : 'Intention is what matters most'}
                </h3>
              </div>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {sv ? 'I Koranläsning handlar allt om niyyah (avsikt). Vår intention är att:' : 'In Quran reading, everything is about niyyah (intention). Our intention is to:'}
              </p>
              <div className="space-y-2">
                {(sv ? ['Komma närmare Allah', 'Lära oss läsa Hans ord korrekt', 'Leva med Koranen i våra liv']
                     : ['Come closer to Allah', 'Learn to read His words correctly', 'Live with the Quran in our lives']).map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Sparkles size={16} className="text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Kunskapens värde */}
            <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <Heart size={22} style={{ color: '#0B3D2E' }} />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Kunskapens värde' : 'The value of knowledge'}
                </h3>
              </div>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {sv ? 'Denna undervisning är inte bara en tjänst \u2014 det är en överföring av kunskap som gått från lärare till elev i generationer. Samtidigt är det viktigt att förstå:'
                     : 'This teaching is not just a service \u2014 it is a transfer of knowledge that has passed from teacher to student for generations. It is also important to understand:'}
              </p>
              <div className="space-y-2">
                {(sv ? ['Lärarens tid, erfarenhet och kunskap har ett värde', 'Undervisningen kräver närvaro, tålamod och kontinuitet', 'Det du får tillbaka är något som stannar med dig för livet']
                     : ['The teacher\'s time, experience and knowledge has value', 'Teaching requires presence, patience and continuity', 'What you receive stays with you for life']).map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle size={16} style={{ color: '#0B3D2E' }} className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Balans + Viktigt */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(206,150,8,0.06) 0%, rgba(206,150,8,0.02) 100%)', border: '2px solid rgba(206,150,8,0.2)' }}>
              <div className="flex items-center gap-3 mb-4">
                <Scale size={22} className="text-gold" />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Balans' : 'Balance'}
                </h3>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                {sv ? 'Detta handlar inte om en vanlig affär. Det är en balans mellan:' : 'This is not an ordinary transaction. It is a balance between:'}
              </p>
              <div className="space-y-2">
                {(sv ? ['Att söka kunskap för Allahs skull', 'Att respektera lärarens arbete och tid']
                     : ['Seeking knowledge for Allah\'s sake', 'Respecting the teacher\'s work and time']).map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <ShieldCheck size={16} className="text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-primary)', border: '2px solid rgba(206,150,8,0.15)' }}>
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck size={22} className="text-gold" />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Viktigt' : 'Important'}
                </h3>
              </div>
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Detta är inte en försäljning av Koranen.' : 'This is not a sale of the Quran.'}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                {sv ? 'Detta är en ersättning för tid, undervisning och vägledning.' : 'This is compensation for time, teaching and guidance.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-8" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Vad ingår i undervisningen?' : 'What is included?'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {(sv ? [
              'Personlig undervisning (1-1)',
              'Direkt korrigering av tajweed',
              'Strukturerad metod (Quran Online Method)',
              'Tydlig progression steg för steg',
              'Tillgång till övningar och material',
              'Flexibla tider',
            ] : [
              'Personal teaching (1-1)',
              'Direct tajweed correction',
              'Structured method (Quran Online Method)',
              'Clear step-by-step progression',
              'Access to exercises and materials',
              'Flexible schedules',
            ]).map((item, i) => (
              <div key={i} className="flex items-center gap-2 p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                <CheckCircle size={16} className="text-gold flex-shrink-0" />
                <span className="text-sm text-left" style={{ color: 'var(--text-secondary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Class Type + Level Tabs */}
      <section className="py-6" style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3 mb-4">
            <button onClick={() => setClassType('group')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${classType === 'group' ? 'bg-gold text-black' : ''}`}
              style={classType !== 'group' ? { backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' } : {}}
              data-testid="type-group">
              <Users size={16} /> {sv ? 'Grupplektion (Rekommenderas)' : 'Group Lesson (Recommended)'}
            </button>
            <button onClick={() => setClassType('private')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${classType === 'private' ? 'bg-gold text-black' : ''}`}
              style={classType !== 'private' ? { backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' } : {}}
              data-testid="type-private">
              <User size={16} /> {sv ? 'Privatlektion Premium (1-on-1)' : 'Private Premium Lesson (1-on-1)'}
            </button>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {LEVEL_ORDER.map(level => {
              const Icon = LEVEL_ICONS[level];
              const isActive = activeLevel === level;
              const info = LEVEL_INFO[language]?.[level] || {};
              return (
                <button key={level} onClick={() => setActiveLevel(level)}
                  className={`flex items-center gap-2.5 px-6 py-3 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-gold text-black' : ''}`}
                  style={!isActive ? { backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' } : {}}
                  data-testid={`level-${level}`}>
                  <Icon size={18} /> {info.title}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Course Info + Calculator */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{levelInfo.title}</h2>
            <p className="text-gold font-medium mb-2">{levelInfo.subtitle}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{levelInfo.desc}</p>
          </div>

          {activeCourse ? (
            <div className="pricing-card featured !p-8 text-center mb-8" data-testid="course-card">
              <h3 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{activeCourse.name}</h3>
              {classType === 'private' && (
                <p className="text-xs text-gold mb-3">{sv ? 'Privatlektion Premium (1-on-1)' : 'Private Premium Lesson (1-on-1)'}</p>
              )}
              {activeCourse.description && <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>{activeCourse.description}</p>}
              <div className="flex items-baseline justify-center gap-2 mb-2">
                {isRange && <span className="text-xl line-through" style={{ color: 'var(--text-muted)' }}>{priceMax}</span>}
                {!isRange && activeCourse?.old_group_price > 0 && <span className="text-xl line-through" style={{ color: 'var(--text-muted)' }}>{activeCourse.old_group_price}</span>}
                <span className="text-5xl font-bold text-gold">{priceMin}</span>
                <span style={{ color: 'var(--text-secondary)' }} className="text-sm">SEK/{sv ? 'lektion' : 'lesson'}</span>
              </div>

              {/* Monthly Calculator */}
              <div className="mt-8 p-5 rounded-lg text-left" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }} data-testid="monthly-calculator">
                <p className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Calculator size={16} className="text-gold" /> {sv ? 'Beräkna månadskostnad' : 'Calculate Monthly Cost'}
                </p>
                <div className="flex items-center gap-4 mb-3">
                  <label className="text-xs" style={{ color: 'var(--text-muted)' }}>{sv ? 'Lektioner per vecka:' : 'Lessons per week:'}</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4].map(n => (
                      <button key={n} onClick={() => setLessonsPerWeek(n)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${lessonsPerWeek === n ? 'bg-gold text-black' : ''}`}
                        style={lessonsPerWeek !== n ? { backgroundColor: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' } : {}}
                        data-testid={`weekly-${n}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Beräknad månadskostnad' : 'Estimated Monthly Cost'}</p>
                  <p className="text-3xl font-bold text-gold" data-testid="monthly-cost">{monthlyCostMin.toLocaleString()} SEK<span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>/{sv ? 'månad' : 'month'}</span></p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    {priceMin} SEK x {lessonsPerWeek} {sv ? 'lektion/vecka' : 'lesson/week'} x 4 {sv ? 'veckor' : 'weeks'}
                  </p>
                </div>
              </div>

              <ul className="space-y-3 my-8 text-left max-w-sm mx-auto">
                {(classType === 'group' ? [
                  sv ? 'Smågrupper (2-5 elever)' : 'Small groups (2-5 students)',
                  sv ? 'Personlig uppmärksamhet' : 'Personal attention',
                  sv ? 'Erfarna kvalificerade lärare' : 'Experienced qualified teachers',
                  sv ? 'Flexibla tider' : 'Flexible schedules',
                  sv ? 'Framstegsspårning via portal' : 'Progress tracking via portal',
                ] : [
                  sv ? 'Helt individuell undervisning' : 'Fully individual teaching',
                  sv ? 'Anpassat tempo och schema' : 'Customized pace and schedule',
                  sv ? 'Erfarna kvalificerade lärare' : 'Experienced qualified teachers',
                  sv ? '100% fokus på dig' : '100% focus on you',
                  sv ? 'Framstegsspårning via portal' : 'Progress tracking via portal',
                ]).map((feature, i) => (
                  <li key={i} className="flex items-center gap-3" style={{ color: 'var(--text-secondary)' }}>
                    <CheckCircle size={16} className="text-gold flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/booking" className="btn-gold inline-flex items-center gap-2 text-lg py-3 px-8" data-testid="apply-btn">
                {sv ? 'Ansök nu' : 'Apply Now'} <ArrowRight size={18} />
              </Link>
            </div>
          ) : (
            <div className="pricing-card !p-8 text-center"><p style={{ color: 'var(--text-muted)' }}>{sv ? 'Laddar...' : 'Loading...'}</p></div>
          )}

          {/* All Courses Quick View */}
          {courses.length > 1 && (
            <div className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {LEVEL_ORDER.map(level => {
                  const course = courses.find(c => c.level === level);
                  const info = LEVEL_INFO[language]?.[level] || {};
                  if (!course) return null;
                  const pMin = classType === 'private' && course.private_enabled ? course.private_price_min : course.price_per_lesson;
                  const pMax = classType === 'private' && course.private_enabled ? course.private_price_max : course.price_per_lesson;
                  return (
                    <div key={level} className={`pricing-card !p-5 cursor-pointer transition-all ${activeLevel === level ? 'border-gold' : ''}`}
                      onClick={() => setActiveLevel(level)} style={activeLevel === level ? { borderColor: 'var(--gold)' } : {}} data-testid={`course-quick-${level}`}>
                      <p className="text-xs text-gold font-medium mb-1">{info.title}</p>
                      <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{course.name}</p>
                      <p className="text-2xl font-bold text-gold">{pMin !== pMax ? <><span className="text-base line-through mr-2" style={{ color: 'var(--text-muted)' }}>{pMax}</span>{pMin}</> : course.old_group_price > 0 ? <><span className="text-base line-through mr-2" style={{ color: 'var(--text-muted)' }}>{course.old_group_price}</span>{pMin}</> : pMin} <span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>SEK</span></p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Mjuk formulering */}
      <section className="py-12" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm italic" style={{ color: 'var(--text-secondary)', lineHeight: 1.9 }}>
            {sv ? 'Jag strävar efter att hålla undervisningen tillgänglig och underlätta för elever som verkligen vill lära sig.'
                 : 'I strive to keep the teaching accessible and facilitate for students who truly want to learn.'}
          </p>
        </div>
      </section>

      {/* Andlig värde + Investering */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Vad du investerar i */}
            <div className="p-6 sm:p-8 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(206,150,8,0.08) 0%, rgba(206,150,8,0.02) 100%)', border: '2px solid rgba(206,150,8,0.2)' }}>
              <div className="flex items-center gap-3 mb-5">
                <Star size={22} className="text-gold" />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Vad du investerar i' : 'What you invest in'}
                </h3>
              </div>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {sv ? 'När du investerar i dessa lektioner investerar du i:' : 'When you invest in these lessons you invest in:'}
              </p>
              <div className="space-y-2">
                {(sv ? ['Din relation med Koranen', 'Din recitation', 'Din förståelse', 'Din framtid']
                     : ['Your relationship with the Quran', 'Your recitation', 'Your understanding', 'Your future']).map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-gold flex-shrink-0" />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Andlig investering */}
            <div className="p-6 sm:p-8 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(11,61,46,0.06) 0%, rgba(11,61,46,0.02) 100%)', border: '2px solid rgba(11,61,46,0.15)' }}>
              <div className="flex items-center gap-3 mb-5">
                <Sparkles size={22} style={{ color: '#0B3D2E' }} />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'En investering för din ākhirah' : 'An investment for your ākhirah'}
                </h3>
              </div>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                {sv ? 'Om ett barn lär sig Koranen idag, kommer det att:' : 'If a child learns the Quran today, they will:'}
              </p>
              <div className="space-y-2 mb-5">
                {(sv ? ['Läsa Koranen i många år framöver', 'Bära med sig denna kunskap genom livet', 'Föra den vidare till nästa generation \u2014 in shāʾ Allāh']
                     : ['Read the Quran for many years to come', 'Carry this knowledge through life', 'Pass it on to the next generation \u2014 in shāʾ Allāh']).map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Sparkles size={14} className="text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                <p className="text-sm italic text-center" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                  {sv ? '\u201DDet du lär dig idag kan bli en ständig belöning (ṣadaqah jāriyah) i ditt liv.\u201D'
                       : '\u201DWhat you learn today can become a continuous reward (ṣadaqah jāriyah) in your life.\u201D'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ borderTop: '1px solid var(--border-color)' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Redo att börja din resa?' : 'Ready to start your journey?'}
          </h2>
          <p className="mb-8 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {sv ? 'Boka en gratis provlektion och börja din resa idag.' : 'Book a free trial lesson and start your journey today.'}
          </p>
          <Link to="/booking" className="btn-gold inline-flex items-center gap-2" data-testid="programs-cta">
            {sv ? 'Boka gratis provlektion' : 'Book free trial lesson'} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;
